"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Check, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { PricingTier } from "@/lib/courses";
import { ACTIVE_PROMO } from "@/lib/courses";

interface PricingTableProps {
  tiers: PricingTier[];
  courseColor: string;
}

function useCountdown(target: Date) {
  const [timeLeft, setTimeLeft] = useState<{ d: number; h: number; m: number; s: number } | null>(null);

  useEffect(() => {
    function calc() {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) { setTimeLeft({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setTimeLeft({
        d: Math.floor(diff / 86_400_000),
        h: Math.floor((diff % 86_400_000) / 3_600_000),
        m: Math.floor((diff % 3_600_000) / 60_000),
        s: Math.floor((diff % 60_000) / 1_000),
      });
    }
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [target]);

  return timeLeft;
}

function pad(n: number) { return String(n).padStart(2, "0"); }

function applyDiscount(price: string, discount: string): string {
  const num = parseFloat(price.replace(/[^0-9.]/g, ""));
  const pct = parseFloat(discount.replace("%", "")) / 100;
  const discounted = Math.round(num * (1 - pct));
  return `$${discounted.toLocaleString("en-US")}`;
}

function splitTierName(name: string): { product: string; variant: string | null } {
  const parenIdx = name.indexOf(" (");
  if (parenIdx === -1) return { product: name, variant: null };
  return {
    product: name.slice(0, parenIdx),
    variant: name.slice(parenIdx + 2, -1),
  };
}

function PricingCard({
  tier,
  courseColor,
}: {
  tier: PricingTier;
  courseColor: string;
}) {
  const [hovered, setHovered] = useState(false);
  const { product, variant } = splitTierName(tier.name);
  const active = hovered || tier.featured;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative flex flex-col rounded-xl border-2 transition-all duration-200 cursor-default"
      style={{
        borderColor: active ? courseColor : "var(--color-cb-border, #1A2332)",
        backgroundColor: active ? "var(--color-cb-surface-2, #151E2E)" : "var(--color-cb-surface, #0F1623)",
        boxShadow: hovered
          ? `0 0 0 1px ${courseColor}30, 0 20px 48px ${courseColor}18`
          : tier.featured
          ? `0 0 0 1px ${courseColor}20`
          : "none",
        transform: hovered ? "translateY(-6px)" : "none",
      }}
    >
      {/* Featured badge */}
      {tier.badge && (
        <div
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap"
          style={{ backgroundColor: courseColor }}
        >
          {tier.badge}
        </div>
      )}

      <div className="p-6 pb-4">
        {/* Tier name: product on line 1, variant on line 2 */}
        <div className="mb-4">
          <p className="text-base font-bold text-cb-text leading-tight">{product}</p>
          {variant && (
            <p
              className="text-sm font-semibold mt-0.5 transition-colors duration-200"
              style={{ color: active ? courseColor : "var(--color-cb-muted, #8B97B0)" }}
            >
              {variant}
            </p>
          )}
        </div>

        {/* Price */}
        {ACTIVE_PROMO ? (
          <div className="mb-6">
            <p className="text-sm text-cb-muted-2 line-through leading-none mb-1">
              {tier.price}
            </p>
            <p
              className="text-4xl font-bold transition-colors duration-200"
              style={{ color: active ? courseColor : "var(--color-cb-text, #EFF3FF)" }}
            >
              {applyDiscount(tier.price, ACTIVE_PROMO.discount)}
            </p>
          </div>
        ) : (
          <p
            className="text-4xl font-bold mb-6 transition-colors duration-200"
            style={{ color: active ? courseColor : "var(--color-cb-text, #EFF3FF)" }}
          >
            {tier.price}
          </p>
        )}

        {/* Features */}
        <ul className="space-y-3 mb-6">
          {tier.features.map((f) => (
            <li key={f.text} className="flex items-start gap-2.5">
              {f.included ? (
                <Check className="h-4 w-4 shrink-0 mt-0.5 text-cb-success" />
              ) : (
                <X className="h-4 w-4 shrink-0 mt-0.5 text-cb-muted-2" />
              )}
              <span
                className={`text-sm leading-snug ${
                  f.included ? "text-cb-muted" : "text-cb-muted-2 line-through"
                }`}
              >
                {f.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="px-6 pb-6 mt-auto">
        {tier.featured || hovered ? (
          <Button className="w-full" size="lg" asChild>
            <Link
              href={ACTIVE_PROMO ? `${tier.enrollUrl}?prefilled_promo_code=${ACTIVE_PROMO.code}` : tier.enrollUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              {tier.ctaLabel ?? "Enroll Now"}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button variant="outline" className="w-full" size="lg" asChild>
            <Link
              href={ACTIVE_PROMO ? `${tier.enrollUrl}?prefilled_promo_code=${ACTIVE_PROMO.code}` : tier.enrollUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              {tier.ctaLabel ?? "Enroll Now"}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

function PromoBanner({ code, discount, expiresAt }: { code: string; discount: string; expiresAt: Date }) {
  const t = useCountdown(expiresAt);
  const expired = t !== null && t.d === 0 && t.h === 0 && t.m === 0 && t.s === 0;
  if (expired) return null;

  return (
    <div className="mb-6 rounded-xl border border-cb-violet/30 bg-cb-violet/10 px-5 py-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <span className="block text-xs font-semibold text-cb-violet-text uppercase tracking-widest font-mono mb-1">
            Limited offer
          </span>
          <span className="text-sm text-cb-muted">
            Use code{" "}
            <span className="font-mono font-bold text-cb-text tracking-wide">{code}</span>{" "}
            for{" "}
            <span className="text-cb-violet-text font-semibold">{discount} off</span>
            {" "}— applied automatically at checkout
          </span>
        </div>
        {t !== null && (
          <div className="flex items-center gap-1.5 font-mono text-xs shrink-0">
            {[
              { label: "d", value: t.d },
              { label: "h", value: t.h },
              { label: "m", value: t.m },
              { label: "s", value: t.s },
            ].map(({ label, value }, i) => (
              <span key={label} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-cb-muted-2">:</span>}
                <span className="flex flex-col items-center">
                  <span className="tabular-nums font-bold text-cb-text text-xl">{pad(value)}</span>
                  <span className="text-cb-muted-2 text-[10px] uppercase tracking-wider">{label}</span>
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function PricingTable({ tiers, courseColor }: PricingTableProps) {
  return (
    <div>
      {ACTIVE_PROMO && (
        <PromoBanner
          code={ACTIVE_PROMO.code}
          discount={ACTIVE_PROMO.discount}
          expiresAt={ACTIVE_PROMO.expiresAt}
        />
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {tiers.map((tier) => (
          <PricingCard key={tier.name} tier={tier} courseColor={courseColor} />
        ))}
      </div>

      {/* Enterprise row */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-cb-violet/25 bg-cb-surface px-6 py-5 hover:border-cb-violet/50 transition-colors">
        <div className="text-center sm:text-left">
          <p className="text-sm font-semibold text-cb-text">Teams &amp; Enterprise</p>
          <p className="text-xs text-cb-muted mt-0.5">
            All Plus features · Dynamic group discount · Custom onboarding · Instant quote
          </p>
        </div>
        <Button variant="outline" size="md" asChild className="shrink-0 min-h-11">
          <Link href="/business" className="flex items-center gap-2">
            Explore team plans
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
